import {debugAssert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';

export const UPDATE_ALL_ITEM_PARTS = -1;

/**
 * Used for items that support incremental updates. This function maps the item to its parts and part IDs.
 */
export type GetItemParts<ItemState, ItemStatePart> = (
  item: ItemState
) => Array<{itemPartId: string; itemPart: ItemStatePart}>;

export interface CidItemPartMap<ItemStatePart> {
  [cid: string]: ItemStatePart;
}

export class ItemPartSaveTracker<ItemState = unknown, ItemStatePart = unknown> {
  private unsavedUpdateMap: CidItemPartMap<ItemStatePart> | typeof UPDATE_ALL_ITEM_PARTS = {};
  private validationUpdateMap: CidItemPartMap<ItemStatePart> | typeof UPDATE_ALL_ITEM_PARTS = {};
  private lastSavedCidIdMap: CidIdMap | undefined;

  constructor(private readonly getItemParts: GetItemParts<ItemState, ItemStatePart> | undefined) {}

  getUnsavedItemParts(): Array<ItemStatePart> | typeof UPDATE_ALL_ITEM_PARTS {
    return this.unsavedUpdateMap !== UPDATE_ALL_ITEM_PARTS
      ? Object.values(this.unsavedUpdateMap)
      : UPDATE_ALL_ITEM_PARTS;
  }

  /**
   * Resets the ItemPartSaveTracker to start with a new CidIdMap as its baseline that it is tracking changes from.
   */
  reset(fileId: string | number, cidIdMap?: CidIdMap): void {
    debugAssert(() => Boolean(cidIdMap), 'No CidIdMap was provided', {extra: {fileId}});
    if (BenchlingConfig.ELN_VALIDATE_NOTE_PART_LEVEL_SAVING) {
      this.validationUpdateMap =
        this.unsavedUpdateMap !== UPDATE_ALL_ITEM_PARTS ? {...this.unsavedUpdateMap} : UPDATE_ALL_ITEM_PARTS;
      this.lastSavedCidIdMap = cidIdMap;
    }
    this.unsavedUpdateMap = {};
  }

  validate(
    fileId: string | number,
    savedCidToIdMap: CidIdMap | undefined,
    cidPrefix: string = 'note-part'
  ): void {
    if (!(typeof fileId === 'string')) {
      debugAssert(() => false, 'fileId provided was not a string', {extra: {fileId}});
      return;
    }
    if (!(APIUtil.isDocument(fileId) || APIUtil.isEntry(fileId) || APIUtil.isEntryTemplate(fileId))) {
      debugAssert(() => false, 'Attempted to validate note part saving for an unexpected model', {
        extra: {fileId},
      });
      return;
    }
    if (
      !BenchlingConfig.ELN_VALIDATE_NOTE_PART_LEVEL_SAVING ||
      !savedCidToIdMap ||
      this.validationUpdateMap === undefined ||
      this.validationUpdateMap === UPDATE_ALL_ITEM_PARTS
    ) {
      return;
    }
    const relevantCids = new Set(Object.keys(savedCidToIdMap).filter((cid) => cid.startsWith(cidPrefix)));
    const validationMap = this.validationUpdateMap ?? {};

    for (const cid of new Set([...relevantCids, ...Object.keys(validationMap)])) {
      const currentSavedId = savedCidToIdMap[cid];
      const lastSavedId = this.lastSavedCidIdMap?.[cid];
      const isMarkedUnsaved = validationMap[cid];
      const wasUpdated = currentSavedId !== lastSavedId;

      if (relevantCids.has(cid) && wasUpdated && !isMarkedUnsaved) {
        logging.info('Item part was updated but was not marked as unsaved.', {
          extra: {
            cid,
            fileId,
            lastSavedId,
            currentSavedId,
          },
        });
      }

      if (isMarkedUnsaved && !wasUpdated) {
        logging.info('Item part was marked as unsaved but was not updated.', {
          extra: {
            cid,
            fileId,
            id: savedCidToIdMap[cid],
          },
        });
      }
    }
  }

  markAllForUpdate(): void {
    this.unsavedUpdateMap = UPDATE_ALL_ITEM_PARTS;
  }

  addUnsavedItemPart(updatedItem: ItemState, updatedItemPartIndexes?: Array<number>): void {
    if (updatedItemPartIndexes === undefined || this.getItemParts == null) {
      return;
    }
    const updatedItemParts = this.getItemParts(updatedItem);
    updatedItemPartIndexes.forEach((itemPartIndex) => {
      const changedItemPart = updatedItemParts?.[itemPartIndex];
      // Mark that this item will require all parts to be sent in the next save
      if (itemPartIndex === UPDATE_ALL_ITEM_PARTS || !changedItemPart) {
        this.unsavedUpdateMap = UPDATE_ALL_ITEM_PARTS;
      } else if (this.unsavedUpdateMap !== UPDATE_ALL_ITEM_PARTS) {
        const {itemPartId, itemPart} = changedItemPart;
        this.unsavedUpdateMap[itemPartId] = itemPart;
      }
    });
  }
}
